/**
 * Predimensionamiento de columnas — sección por carga axial.
 *
 * Método clásico de la construcción peruana:
 *   P_servicio = α · A_tributaria · w · n_pisos       (kgf)
 *   A_concreto = P_servicio / (k · f'c)               (cm²)
 *
 * Donde:
 *   α  factor de mayoración por tipo de columna (interior, perimetral, esquinera)
 *   w  carga repartida por m² del edificio (~1000 kgf/m² para viviendas)
 *   k  fracción de f'c utilizable axialmente (~0.45 para edificio simétrico,
 *      0.35 para columnas más cargadas/eccentricas)
 */

import { useState } from 'react';
import { PredimCard, Group, NumberRow, ResultRow, FormulaNote, roundTo } from './shared';
import { ColumnSectionDiagram, DiagramPanel, View2D3DToggle } from './diagrams';
import { Column3D } from './views3d';
import { PredimToolbar } from './PredimToolbar';
import type { DesignFull } from '@/features/designs/types';

type Variant = 'central' | 'perimeter' | 'corner' | 'bridge';

interface PredimColumnProps {
  variant: Variant;
  /** Seed values when opening a saved design — partial so missing keys
   *  fall back to the hardcoded defaults. */
  initialInputs?: {
    A_trib?: number;
    w_servicio?: number;
    n_pisos?: number;
    fc?: number;
    shape?: 'square' | 'rect_1_15' | 'rect_1_2';
  } | null;
  /** Existing design row when editing — turns Save into Update. */
  existing?: DesignFull | null;
}

const VARIANT_PRESETS: Record<Variant, { alpha: number; k: number; label: string; descr: string }> = {
  central: {
    alpha: 1.10, k: 0.45,
    label: 'Columna central',
    descr: 'Interior del pórtico, recibe carga axial casi pura. α=1.10, k=0.45·f\'c.',
  },
  perimeter: {
    alpha: 1.25, k: 0.35,
    label: 'Columna perimetral',
    descr: 'Borde del edificio, recibe momento adicional. α=1.25, k=0.35·f\'c.',
  },
  corner: {
    alpha: 1.50, k: 0.35,
    label: 'Columna esquinera',
    descr: 'Esquina, recibe biaxial. α=1.50, k=0.35·f\'c.',
  },
  bridge: {
    alpha: 1.10, k: 0.45,
    label: 'Columna de puente',
    descr: 'Pilas de puentes (Carga vertical dominante). Estimación inicial.',
  },
};

export function PredimColumn({ variant, initialInputs, existing }: PredimColumnProps) {
  const preset = VARIANT_PRESETS[variant];
  // Seed from initialInputs (loaded design) or fall back to sensible defaults.
  const [A_trib, setATrib] = useState(initialInputs?.A_trib ?? 20);        // m²
  const [w, setW] = useState(initialInputs?.w_servicio ?? 1000);            // kgf/m²
  const [n_pisos, setNPisos] = useState(initialInputs?.n_pisos ?? 5);
  const [fc, setFc] = useState(initialInputs?.fc ?? 210);                   // kgf/cm²
  const [shape, setShape] = useState<'square' | 'rect_1_15' | 'rect_1_2'>(
    initialInputs?.shape ?? 'square',
  );

  // Computations
  const P_servicio = preset.alpha * A_trib * w * n_pisos;     // kgf
  const Ac_required = P_servicio / (preset.k * fc);           // cm²
  // Choose b × h according to the shape: square (b=h), 1:1.5 or 1:2
  let b: number, h: number;
  if (shape === 'square') {
    const side = Math.sqrt(Ac_required);
    b = h = roundTo(side, 5);
  } else if (shape === 'rect_1_15') {
    // b = h / 1.5 → Ac = b*h = h²/1.5 → h = sqrt(1.5·Ac)
    const h_ = Math.sqrt(1.5 * Ac_required);
    h = roundTo(h_, 5);
    b = roundTo(h_ / 1.5, 5);
  } else {
    const h_ = Math.sqrt(2 * Ac_required);
    h = roundTo(h_, 5);
    b = roundTo(h_ / 2, 5);
  }
  const minDim = Math.min(b, h);
  const meetsMin = minDim >= 25;

  const exportInputs = {
    column_variant: variant,
    A_trib, w_servicio: w, n_pisos, fc, shape,
  };

  const [view, setView] = useState<'2D' | '3D'>('2D');
  // Approximate column height for visualization: 3 m per floor
  const hCol = Math.max(n_pisos, 1) * 280;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex justify-end">
        <PredimToolbar
          family="columns"
          variant={variant}
          inputs={exportInputs}
          existing={existing}
        />
      </div>
      <PredimCard title={preset.label} subtitle={preset.descr}>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Group title="Geometría tributaria">
            <NumberRow label="Área tributaria A_t" value={A_trib} onChange={setATrib} step={0.5} unit="m²" />
            <NumberRow label="N° de pisos arriba" value={n_pisos} onChange={(v) => setNPisos(Math.max(1, Math.round(v)))} step={1} unit="pisos" />
          </Group>

          <Group title="Cargas y materiales">
            <NumberRow label="Carga servicio por m²" value={w} onChange={setW} step={50} unit="kgf/m²" />
            <NumberRow label="f'c" value={fc} onChange={setFc} step={10} unit="kgf/cm²" />
            <label className="flex items-center justify-between gap-3 text-[12px]">
              <span style={{ color: 'var(--text-secondary)' }}>Relación b : h</span>
              <select
                value={shape}
                onChange={(e) => setShape(e.target.value as typeof shape)}
                className="w-[164px] rounded-[var(--radius-sm)] border px-2 py-1 text-[12px]"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              >
                <option value="square">Cuadrada (b = h)</option>
                <option value="rect_1_15">Rectangular (1 : 1.5)</option>
                <option value="rect_1_2">Rectangular (1 : 2)</option>
              </select>
            </label>
          </Group>
        </div>

        <FormulaNote>
          <div className="font-mono text-[10px] mb-1">P_serv = α · A_t · w · n_pisos &nbsp;|&nbsp; A_c = P_serv / (k · f'c)</div>
          α = {preset.alpha.toFixed(2)} (factor por tipo) &nbsp;·&nbsp; k = {preset.k.toFixed(2)} (fracción de f'c)
        </FormulaNote>
      </PredimCard>

      <PredimCard title="Resultados">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_auto]">
          <div className="space-y-1">
            <ResultRow label="P servicio total" value={`${(P_servicio / 1000).toFixed(2)} tonf`} />
            <ResultRow label="Área de concreto requerida" value={`${Ac_required.toFixed(0)} cm²`} />
            <ResultRow label="Sección b × h sugerida" value={`${b} × ${h} cm`} highlight />
            <ResultRow label="Área provisto" value={`${b * h} cm² (${b * h >= Ac_required ? '≥' : '<'} requerido)`} />
          </div>
          <DiagramPanel
            title={view === '3D' ? 'Vista 3D de la columna' : 'Sección transversal sugerida'}
            toolbar={<View2D3DToggle value={view} onChange={setView} />}
          >
            {view === '3D' ? (
              <div style={{ width: 380, height: 320 }}>
                <Column3D b={b} h={h} hCol={hCol} />
              </div>
            ) : (
              <ColumnSectionDiagram b={b} h={h} cover={4} />
            )}
          </DiagramPanel>
        </div>

        {!meetsMin && (
          <div className="mt-3 rounded-[var(--radius-sm)] border p-2.5 text-[11px]"
            style={{
              background: 'rgba(245,158,11,0.08)',
              borderColor: 'rgba(245,158,11,0.40)',
              color: 'var(--text-primary)',
            }}>
            <strong style={{ color: 'var(--accent)' }}>Atención:</strong> el lado menor ({minDim} cm) está por
            debajo del mínimo práctico (25 cm). Eleva la dimensión menor a 25 cm.
          </div>
        )}

        <div className="mt-3 text-[11px] leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          Este valor es solo el dimensionamiento <strong>preliminar</strong>. Para verificación
          completa con momentos, capacidad P-M y refuerzo, usar el módulo <strong>Columnas</strong>.
        </div>
      </PredimCard>
    </div>
  );
}
